import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import moment from "moment";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css"; // Import Sun Editor's CSS File
import Image from "../../components/Images/Image";
import { MAX_IMAGE_SIZE } from "../../lib/config";
import {
  showToast,
  startLoader,
  stopLoader,
  UploadImage,
} from "../../lib/global";
import {
  createNewPage,
  getPageById,
  updatePageById,
} from "../../redux/actions/pages.action";
import { breadcrumbAction } from "../../redux/actions/config.action";
import { IOSSwitch } from "../../components/switch/IOSSwitch";
import { getAllCollections } from "../../redux/actions/collections.action";

function AddPage() {
  const { type, pageId } = useParams(); // type = 'section_promotion', 'deal', 'page'
  const dispatch = useDispatch();
  const history = useHistory();

  // Form states
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [isSlugManual, setIsSlugManual] = useState(false);
  const [status, setStatus] = useState("draft"); // publish or draft
  const [content, setContent] = useState("");
  const [orderColumn, setOrderColumn] = useState(0);
  const [publishDate, setPublishDate] = useState(moment().format("YYYY-MM-DDTHH:mm"));

  // SEO states
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");

  // Image states (Desktop Banner)
  const [bannerDesktopFile, setBannerDesktopFile] = useState(null);
  const [bannerDesktopUrl, setBannerDesktopUrl] = useState("");

  // Image states (Mobile Banner)
  const [bannerMobileFile, setBannerMobileFile] = useState(null);
  const [bannerMobileUrl, setBannerMobileUrl] = useState("");

  // Image states (Meta Image)
  const [metaImageFile, setMetaImageFile] = useState(null);
  const [metaImageUrl, setMetaImageUrl] = useState("");

  const [collections, setCollections] = useState([]);
  const [selectedCollectionIds, setSelectedCollectionIds] = useState([]);
  const [productCount, setProductCount] = useState(0);

  const getPageLabel = () => {
    if (type === "section_promotion") return "Section Promotion";
    if (type === "deal") return "Deals Promotion";
    return "Static Page";
  };

  const getParentLabel = () => {
    if (type === "section_promotion") return "Section Promotions";
    if (type === "deal") return "Deals Promotions";
    return "Pages";
  };

  const slugify = (text) => {
    return text
      .toString()
      .toLowerCase()
      .replace(/\s+/g, "-") // Replace spaces with -
      .replace(/[^\w\-]+/g, "") // Remove all non-word chars
      .replace(/\-\-+/g, "-") // Replace multiple - with single -
      .replace(/^-+/, "") // Trim - from start of text
      .replace(/-+$/, ""); // Trim - from end of text
  };

  const handleTitleChange = (e) => {
    const val = e.target.value;
    setTitle(val);
    if (!isSlugManual && !pageId) {
      setSlug(slugify(val));
    }
  };

  const handleSlugChange = (e) => {
    setSlug(slugify(e.target.value));
    setIsSlugManual(true);
  };

  useEffect(() => {
    const parentLabel = getParentLabel();
    const pageLabel = getPageLabel();
    const breadcrumb = [
      { label: parentLabel, url: `/pages/${type}` },
      { label: pageId ? `Edit ${pageLabel}` : `Add ${pageLabel}`, url: null },
    ];
    dispatch(breadcrumbAction(breadcrumb));

    if (type === "section_promotion") {
      dispatch(getAllCollections({ limit: 9999, offset: 0 }))
        .then((res) => {
          if (res?.data?.status === 200) {
            setCollections(res.data.data || []);
          }
        })
        .catch(() => setCollections([]));
    }

    if (pageId) {
      startLoader();
      dispatch(getPageById(pageId))
        .then((res) => {
          stopLoader();
          if (res && res.data && res.data.status === 200) {
            const data = res.data.data;
            setTitle(data.title || "");
            setSlug(data.slug || "");
            setIsSlugManual(true);
            setStatus(data.status || "draft");
            setContent(data.content || "");
            setOrderColumn(data.order_column !== undefined ? data.order_column : 0);
            if (data.publish_date) {
              setPublishDate(moment(data.publish_date).format("YYYY-MM-DDTHH:mm"));
            }
            setMetaTitle(data.meta_title || "");
            setMetaDescription(data.meta_description || "");
            setBannerDesktopUrl(data.banner_desktop || "");
            setBannerMobileUrl(data.banner_mobile || "");
            setMetaImageUrl(data.meta_image || "");
            if (type === "section_promotion") {
              setSelectedCollectionIds(data.collection_ids || []);
              setProductCount(data.product_count || 0);
            }
          }
        })
        .catch((err) => {
          stopLoader();
          console.error("Error fetching page details:", err);
          showToast("Failed to fetch page details", "error");
        });
    }
  }, [pageId, type, dispatch]);

  const toggleCollection = (collectionId) => {
    setSelectedCollectionIds((prev) => {
      const next = prev.includes(collectionId)
        ? prev.filter((id) => id !== collectionId)
        : [...prev, collectionId];
      const count = next.reduce((sum, id) => {
        const col = collections.find((c) => c.collection_id === id);
        return sum + (col?.product_count || 0);
      }, 0);
      setProductCount(count);
      return next;
    });
  };

  const handleUploadFile = (e, target) => {
    const fileData = e.target.files[0];
    if (!fileData) return;

    if (fileData.size > MAX_IMAGE_SIZE) {
      showToast(
        `Image with size more than ${
          MAX_IMAGE_SIZE / 1000000
        }MB is not supported.\nPlease upload image of size smaller than ${
          MAX_IMAGE_SIZE / 1000000
        }MB.`,
        "error"
      );
      return;
    }

    if (target === "desktop") setBannerDesktopFile(fileData);
    if (target === "mobile") setBannerMobileFile(fileData);
    if (target === "meta") setMetaImageFile(fileData);

    const reader = new FileReader();
    reader.readAsDataURL(fileData);
    reader.onload = function () {
      if (target === "desktop") setBannerDesktopUrl(reader.result);
      if (target === "mobile") setBannerMobileUrl(reader.result);
      if (target === "meta") setMetaImageUrl(reader.result);
    };
    reader.onerror = function (error) {
      console.error("FileReader Error: ", error);
    };
  };

  const validateForm = () => {
    if (!title.trim()) {
      showToast("Title is required", "error");
      return false;
    }
    if (!slug.trim()) {
      showToast("Slug is required", "error");
      return false;
    }
    if (orderColumn === "" || orderColumn === null) {
      showToast("Order column is required", "error");
      return false;
    }
    if (!publishDate) {
      showToast("Publish date is required", "error");
      return false;
    }
    if (!bannerDesktopFile && !bannerDesktopUrl) {
      showToast("Desktop banner image is required", "error");
      return false;
    }
    if (type === "section_promotion" && selectedCollectionIds.length === 0) {
      showToast("At least one collection must be assigned", "error");
      return false;
    }
    return true;
  };

  const onSubmit = async () => {
    if (!validateForm()) return;

    startLoader();

    try {
      let finalBannerDesktop = bannerDesktopUrl;
      let finalBannerMobile = bannerMobileUrl;
      let finalMetaImage = metaImageUrl;

      if (bannerDesktopFile) {
        const res = await UploadImage(bannerDesktopFile, "pages");
        finalBannerDesktop = typeof res === "string" ? res : res.secure_url;
      }
      if (bannerMobileFile) {
        const res = await UploadImage(bannerMobileFile, "pages");
        finalBannerMobile = typeof res === "string" ? res : res.secure_url;
      }
      if (metaImageFile) {
        const res = await UploadImage(metaImageFile, "pages");
        finalMetaImage = typeof res === "string" ? res : res.secure_url;
      }

      const payload = {
        title,
        slug,
        type,
        status,
        content,
        order_column: parseInt(orderColumn, 10),
        publish_date: moment(publishDate).toISOString(),
        meta_title: metaTitle,
        meta_description: metaDescription,
        banner_desktop: finalBannerDesktop,
        banner_mobile: finalBannerMobile,
        meta_image: finalMetaImage,
        ...(type === "section_promotion" ? { collection_ids: selectedCollectionIds } : {}),
      };

      if (pageId) {
        dispatch(updatePageById(pageId, payload))
          .then((res) => {
            stopLoader();
            if (res && res.data && res.data.status === 200) {
              showToast("Page updated successfully", "success");
              history.push(`/pages/${type}`);
            } else {
              showToast(res?.data?.message || "Failed to update page", "error");
            }
          })
          .catch((err) => {
            stopLoader();
            console.error("Error updating page:", err);
            showToast("Failed to update page", "error");
          });
      } else {
        dispatch(createNewPage(payload))
          .then((res) => {
            stopLoader();
            if (res && res.data && res.data.status === 201) {
              showToast("Page created successfully", "success");
              history.push(`/pages/${type}`);
            } else {
              showToast(res?.data?.message || "Failed to create page", "error");
            }
          })
          .catch((err) => {
            stopLoader();
            console.error("Error creating page:", err);
            showToast("Failed to create page", "error");
          });
      }
    } catch (err) {
      stopLoader();
      console.error("Error uploading assets:", err);
      showToast("Error processing upload assets", "error");
    }
  };

  return (
    <React.Fragment>
      <div className="card m-4 p-4 shadow-sm border-0">
        <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
          <h5 className="font-weight-bold text-uppercase mb-0">
            {pageId ? `Edit ${getPageLabel()}` : `Create New ${getPageLabel()}`}
          </h5>
        </div>

        <div className="row">
          {/* Main Form Fields */}
          <div className="col-lg-8 pr-lg-4">
            <div className="form-group mb-3">
              <label className="font-weight-bold imp mb-1">Title</label>
              <input
                type="text"
                placeholder="Page Title"
                value={title}
                className="form-control"
                onChange={handleTitleChange}
              />
            </div>

            <div className="form-group mb-3">
              <label className="font-weight-bold imp mb-1">URL Slug</label>
              <input
                type="text"
                placeholder="url-slug-format"
                value={slug}
                className="form-control"
                onChange={handleSlugChange}
              />
              <small className="text-muted">Slug is automatically created from title. Keep it URL-friendly.</small>
            </div>

            <div className="row">
              <div className="col-md-6 form-group mb-3">
                <label className="font-weight-bold imp mb-1">Order Column</label>
                <input
                  type="number"
                  placeholder="Order Sequence"
                  value={orderColumn}
                  className="form-control"
                  onChange={(e) => setOrderColumn(e.target.value)}
                />
              </div>
              <div className="col-md-6 form-group mb-3">
                <label className="font-weight-bold imp mb-1">Publish Date & Time</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={publishDate}
                  onChange={(e) => setPublishDate(e.target.value)}
                />
              </div>
            </div>

            {type === "section_promotion" && (
              <div className="form-group mb-4">
                <label className="font-weight-bold imp mb-1">Assign Collections</label>
                <p className="small text-muted mb-2">
                  Select product collections linked to this section promotion page.
                </p>
                {collections.length === 0 ? (
                  <p className="text-muted small mb-0">
                    No collections found. Create collections under Products first.
                  </p>
                ) : (
                  <div className="border rounded-lg p-2" style={{ maxHeight: 220, overflowY: "auto" }}>
                    {collections.map((collection) => {
                      const selected = selectedCollectionIds.includes(collection.collection_id);
                      return (
                        <label
                          key={collection.collection_id}
                          className={`d-flex align-items-center justify-content-between mb-2 p-2 rounded ${
                            selected ? "bg-light" : ""
                          }`}
                          style={selected ? { backgroundColor: "#e8f2fc" } : undefined}
                        >
                          <span className="d-flex align-items-center mb-0">
                            <input
                              type="checkbox"
                              className="mr-2"
                              checked={selected}
                              onChange={() => toggleCollection(collection.collection_id)}
                            />
                            {collection.name}
                          </span>
                          <span className="text-muted small">{collection.product_count || 0}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <div className="d-flex justify-content-between align-items-center bg-light rounded p-2 mt-2">
                  <span className="text-muted small">Total products in assigned collections</span>
                  <strong>{productCount}</strong>
                </div>
              </div>
            )}

            <div className="form-group mb-4">
              <label className="font-weight-bold imp mb-1">Page Content (Rich Text Editor)</label>
              <div className="border rounded">
                <SunEditor
                  height="350px"
                  width="100%"
                  lang="en"
                  autoFocus={false}
                  setContents={content}
                  onChange={(val) => setContent(val)}
                  placeholder="Type page contents here..."
                  name="content_editor"
                  charCounter={true}
                  setOptions={{
                    buttonList: [
                      ["undo", "redo"],
                      ["font", "fontSize", "formatBlock"],
                      ["bold", "underline", "italic", "strike", "subscript", "superscript", "removeFormat"],
                      ["fontColor", "hiliteColor", "outdent", "indent", "align", "horizontalRule", "list", "table"],
                      ["link", "image", "video", "fullScreen", "showBlocks"],
                    ],
                  }}
                />
              </div>
            </div>

            {/* SEO Section */}
            <div className="card bg-light p-3 border rounded mb-4">
              <h6 className="font-weight-bold text-secondary border-bottom pb-2 mb-3">SEO METADATA (OPTIONAL)</h6>
              <div className="form-group mb-3">
                <label className="font-weight-bold mb-1">Meta Title</label>
                <input
                  type="text"
                  placeholder="For SEO title tags"
                  value={metaTitle}
                  className="form-control"
                  onChange={(e) => setMetaTitle(e.target.value)}
                />
              </div>
              <div className="form-group mb-3">
                <label className="font-weight-bold mb-1">Meta Description</label>
                <textarea
                  placeholder="For SEO search description text snippet"
                  value={metaDescription}
                  className="form-control"
                  rows="3"
                  onChange={(e) => setMetaDescription(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Sidebar Upload Fields */}
          <div className="col-lg-4 border-left pl-lg-4">
            <div className="form-group mb-4 d-flex align-items-center justify-content-between border-bottom pb-3">
              <label className="font-weight-bold mb-0">Publishing Status</label>
              <div className="d-flex align-items-center">
                <span className="mr-2 font-weight-bold text-muted small">{status === "publish" ? "Published" : "Draft"}</span>
                <IOSSwitch
                  checked={status === "publish"}
                  onChange={(e) => setStatus(e.target.checked ? "publish" : "draft")}
                />
              </div>
            </div>

            {/* Desktop Banner */}
            <div className="mb-4">
              <label className="font-weight-bold imp mb-1 d-block">
                Desktop Banner <span className="text-muted">(Recommended: 1200 x 400 px)</span>
              </label>
              <input
                accept="image/*"
                id="desktop-banner-upload"
                className="d-none"
                onChange={(e) => handleUploadFile(e, "desktop")}
                type="file"
              />
              <div className="border rounded p-3 text-center bg-light">
                <label htmlFor="desktop-banner-upload" className="pointer m-0 d-block">
                  <Image
                    src={bannerDesktopUrl || "/icons/upload_icon.svg"}
                    alt="desktop preview"
                    style={{
                      width: "100%",
                      maxHeight: "150px",
                      objectFit: "contain",
                      backgroundColor: "#f5f5f5",
                    }}
                  />
                  <div className="mt-2 text-primary font-weight-bold small">
                    {bannerDesktopFile || bannerDesktopUrl ? "Change Desktop Banner" : "Upload Desktop Banner"}
                  </div>
                </label>
              </div>
            </div>

            {/* Mobile Banner */}
            <div className="mb-4">
              <label className="font-weight-bold mb-1 d-block">
                Mobile Banner <span className="text-muted">(Optional, Recommended: 600 x 300 px)</span>
              </label>
              <input
                accept="image/*"
                id="mobile-banner-upload"
                className="d-none"
                onChange={(e) => handleUploadFile(e, "mobile")}
                type="file"
              />
              <div className="border rounded p-3 text-center bg-light">
                <label htmlFor="mobile-banner-upload" className="pointer m-0 d-block">
                  <Image
                    src={bannerMobileUrl || "/icons/upload_icon.svg"}
                    alt="mobile preview"
                    style={{
                      width: "100%",
                      maxHeight: "150px",
                      objectFit: "contain",
                      backgroundColor: "#f5f5f5",
                    }}
                  />
                  <div className="mt-2 text-primary font-weight-bold small">
                    {bannerMobileFile || bannerMobileUrl ? "Change Mobile Banner" : "Upload Mobile Banner"}
                  </div>
                </label>
              </div>
            </div>

            {/* Meta Image */}
            <div className="mb-4">
              <label className="font-weight-bold mb-1 d-block">
                SEO Share Image <span className="text-muted">(Optional, meta_image)</span>
              </label>
              <input
                accept="image/*"
                id="meta-image-upload"
                className="d-none"
                onChange={(e) => handleUploadFile(e, "meta")}
                type="file"
              />
              <div className="border rounded p-3 text-center bg-light">
                <label htmlFor="meta-image-upload" className="pointer m-0 d-block">
                  <Image
                    src={metaImageUrl || "/icons/upload_icon.svg"}
                    alt="meta preview"
                    style={{
                      width: "100%",
                      maxHeight: "150px",
                      objectFit: "contain",
                      backgroundColor: "#f5f5f5",
                    }}
                  />
                  <div className="mt-2 text-primary font-weight-bold small">
                    {metaImageFile || metaImageUrl ? "Change SEO Image" : "Upload SEO Image"}
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer controls */}
      <footer className="d-flex position-sticky b-0 flex-wrap justify-content-end align-items-center py-3 mt-3 bg-white border-top">
        <div className="col-md-12">
          <div className="d-flex justify-content-end pr-3">
            <button
              type="button"
              className="btn btn-outline-danger px-4 mr-2"
              onClick={history.goBack}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-dark px-4"
              onClick={onSubmit}
            >
              {pageId ? "Save Changes" : `Create ${getPageLabel()}`}
            </button>
          </div>
        </div>
      </footer>
    </React.Fragment>
  );
}

export default AddPage;
